import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { CANONICAL_SPECTRUM } from '../utils/chartUtils';

/** TopoJSON `properties.name` → USPS (aligned with us-atlas states-10m). */
export const STATE_NAME_TO_ABBR = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', 'District of Columbia': 'DC', Florida: 'FL',
  Georgia: 'GA', Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA',
  Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD',
  Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS', Missouri: 'MO',
  Montana: 'MT', Nebraska: 'NE', Nevada: 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH',
  Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT',
  Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY'
};

const OBJECTION_RAMP = [
  '#a3d7c4', // Lightest (Lowest intensity)
  '#85c2ad',
  '#66ae96',
  '#459a80',
  '#1c866a'  // Darkest (Highest intensity)
];

function choroplethFill(count, maxV) {
  if (count == null || count <= 0 || !maxV || maxV <= 0) return 'var(--c-white)'; // Unified cool dashboard neutral
  const n = OBJECTION_RAMP.length;
  const t = Math.min(1, Math.max(0, count / maxV));
  const idx = Math.min(n - 1, Math.floor(t * n));
  return OBJECTION_RAMP[idx];
}

const US_ATLAS_STATES = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

/**
 * US choropleth: objection_count per state from dashboard `map_by_state`.
 */
export function ObjectionChoroplethMap({ mapByState }) {
  const byAbbr = useMemo(() => {
    const m = {};
    for (const r of mapByState || []) {
      const abbr = String(r.state_abbr || r.state || '').trim().toUpperCase();
      if (abbr) m[abbr] = r;
    }
    return m;
  }, [mapByState]);

  const maxCount = useMemo(
    () => Math.max(0, ...Object.values(byAbbr).map((r) => Number(r.objection_count) || 0)),
    [byAbbr]
  );

  const [tip, setTip] = useState({ show: false, x: 0, y: 0, name: '', row: null });
  const wrapRef = useRef(null);

  const onMove = useCallback((event, geo) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const name = geo.properties.name;
    const abbr = STATE_NAME_TO_ABBR[name];
    const row = abbr ? byAbbr[abbr] : null;
    setTip({
      show: true,
      x: event.clientX,
      y: event.clientY,
      name,
      row
    });
  }, [byAbbr]);

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', fontSize: '11px', color: 'var(--text3)' }}>
        <span>0</span>
        <div style={{ display: 'flex', height: '7px', borderRadius: '3px', overflow: 'hidden', width: 'min(200px, 100%)' }}>
          {OBJECTION_RAMP.map((c, i) => (
            <div key={i} style={{ flex: 1, background: c }} />
          ))}
        </div>
        <span>{maxCount > 0 ? `${maxCount}` : '0'}{maxCount > 0 ? '+' : ''}</span>
        <span style={{ marginLeft: '4px' }}>— hover state for details</span>
      </div>
      <div style={{ width: '100%', maxWidth: '640px', margin: '0 auto' }}>
        <ComposableMap projection="geoAlbersUsa" style={{ width: '100%', height: 'auto' }}>
          <Geographies geography={US_ATLAS_STATES}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const name = geo.properties.name;
                const abbr = STATE_NAME_TO_ABBR[name];
                const row = abbr ? byAbbr[abbr] : null;
                const count = row ? Number(row.objection_count) || 0 : 0;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={choroplethFill(count, maxCount)}
                    stroke="var(--text2)"
                    strokeWidth={0.75}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none', fill: row ? '#1c866a' : 'var(--border)', cursor: row ? 'pointer' : 'default' },
                      pressed: { outline: 'none' }
                    }}
                    onMouseMove={(e) => onMove(e, geo)}
                    onMouseLeave={() => setTip((t) => ({ ...t, show: false }))}
                  />
                );
              })}
          </Geographies>
        </ComposableMap>
      </div>
      {tip.show ? (
        <div
          className="map-hover-tooltip"
          style={{
            left: tip.x + 15,
            top: tip.y + 15
          }}
        >
          {tip.row ? (
            <>
              <div style={{ fontWeight: 700 }}>{tip.name}</div>
              <div>
                {tip.row.objection_count} objections · {tip.row.filing_count} filings
              </div>
              <div style={{ color: 'var(--text2)', marginTop: '4px' }}>
                {tip.row.avg_disposition_days != null ? `avg ${tip.row.avg_disposition_days}d` : 'avg disposition —'}
                {tip.row.min_disposition_days != null
                  ? ` · range ${tip.row.min_disposition_days}–${tip.row.max_disposition_days}d`
                  : ''}
              </div>
            </>
          ) : (
            <div>{tip.name}: no data</div>
          )}
        </div>
      ) : null}
      <p style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '10px', lineHeight: 1.45 }}>
        Gray = no objections in dataset for that state. Ramp scales to max ({maxCount}) in this view.
      </p>
    </div>
  );
}

function sortDispositionRows(rows, mode) {
  const copy = [...rows];
  const byName = (a, b) =>
    String(a.state_name || a.state || '').localeCompare(String(b.state_name || b.state || ''));
  if (mode === 'avg') {
    // Ascending makes "avg days" visually obvious even on small datasets.
    copy.sort((a, b) => {
      const d = (Number(a.avg_disposition_days) || 0) - (Number(b.avg_disposition_days) || 0);
      return d !== 0 ? d : byName(a, b);
    });
  } else if (mode === 'max') {
    copy.sort((a, b) => {
      const d = (Number(b.max_disposition_days) || 0) - (Number(a.max_disposition_days) || 0);
      return d !== 0 ? d : byName(a, b);
    });
  } else if (mode === 'range') {
    copy.sort((a, b) => {
      const d =
        ((b.max_disposition_days - b.min_disposition_days) || 0) -
        ((a.max_disposition_days - a.min_disposition_days) || 0);
      return d !== 0 ? d : byName(a, b);
    });
  } else {
    copy.sort(byName);
  }
  return copy;
}

/**
 * Canvas range plot (mock chart 3): min–max bar + avg dot per state; two columns on wide layouts.
 */
export function ObjectionDispositionRangeCanvas({ disposition }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [sort] = useState('avg');
  const [dim, setDim] = useState({ w: 700 });
  const rows = useMemo(
    () => disposition.filter((r) => r.min_disposition_days != null && r.max_disposition_days != null),
    [disposition]
  );
  const sorted = useMemo(() => sortDispositionRows(rows, sort), [rows, sort]);
  const [tip, setTip] = useState({ show: false, x: 0, y: 0, lines: [] });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setDim({ w: el.clientWidth || 700 }));
    ro.observe(el);
    setDim({ w: el.clientWidth || 700 });
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap || sorted.length === 0) return;

    const ROW = 38;
    const PAD = { t: 14, r: 64, b: 36, l: 52 };
    const W = Math.max(320, dim.w - 8);
    const colCount = W >= 540 ? 2 : 1;
    const chunk = Math.ceil(sorted.length / colCount);
    const maxRows = chunk;
    const colW = Math.floor(W / colCount);
    const H = PAD.t + maxRows * ROW + PAD.b;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const globalMax = Math.max(...sorted.map((s) => Number(s.max_disposition_days) || 0), 1);
    const axMax = Math.ceil((globalMax * 1.12) / 5) * 5;

    const rowHits = [];

    const drawCol = (states, offsetX) => {
      const pw = colW - PAD.l - PAD.r;
      const xS = (v) => offsetX + PAD.l + (v / axMax) * pw;

      const step = axMax <= 20 ? 5 : axMax <= 40 ? 10 : Math.max(5, Math.ceil(axMax / 5));
      ctx.font = "14px ui-monospace, SFMono-Regular, Menlo, monospace";
      for (let t = 0; t <= axMax; t += step) {
        const x = xS(t);
        ctx.strokeStyle = 'rgba(0,0,0,0.06)';
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(x, PAD.t);
        ctx.lineTo(x, PAD.t + states.length * ROW);
        ctx.stroke();
        ctx.fillStyle = '#9e9e96';
        ctx.textAlign = 'center';
        ctx.fillText(`${t}d`, x, H - PAD.b + 16);
      }

      if (offsetX > 0) {
        ctx.strokeStyle = 'rgba(0,0,0,0.07)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(offsetX - 1, PAD.t - 8);
        ctx.lineTo(offsetX - 1, PAD.t + states.length * ROW + 4);
        ctx.stroke();
      }

      states.forEach((s, i) => {
        const y = PAD.t + i * ROW + ROW / 2;
        const min = Number(s.min_disposition_days);
        const max = Number(s.max_disposition_days);
        const avg = Number(s.avg_disposition_days);
        const xMin = xS(min);
        const xMax = xS(max);
        const xAvg = xS(avg);

        if (i % 2 === 0) {
          ctx.fillStyle = 'rgba(0,0,0,0.018)';
          ctx.fillRect(offsetX, PAD.t + i * ROW, colW, ROW);
        }

        const barColor = 'var(--c-orange)';
        ctx.strokeStyle = `${barColor}99`;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(xMin, y);
        ctx.lineTo(xMax, y);
        ctx.stroke();

        ctx.strokeStyle = '#B4B2A9';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(xMin, y - 5);
        ctx.lineTo(xMin, y + 5);
        ctx.stroke();

        ctx.strokeStyle = 'var(--text3)0';
        ctx.beginPath();
        ctx.moveTo(xMax, y - 5);
        ctx.lineTo(xMax, y + 5);
        ctx.stroke();

        ctx.fillStyle = 'var(--bg-orange-soft)';
        ctx.strokeStyle = 'var(--c-orange)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(xAvg, y, 5.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        const lab = s.state_abbr || s.state || '';
        ctx.fillStyle = '#56564f';
        ctx.font = "bold 15px ui-monospace, SFMono-Regular, Menlo, monospace";
        ctx.textAlign = 'right';
        ctx.fillText(lab, offsetX + PAD.l - 8, y + 5);

        ctx.fillStyle = '#56564f';
        ctx.font = "14px ui-monospace, SFMono-Regular, Menlo, monospace";
        ctx.textAlign = 'left';
        ctx.fillText(`${avg}d`, xMax + 8, y + 5);

        rowHits.push({
          s,
          y0: PAD.t + i * ROW,
          y1: PAD.t + (i + 1) * ROW,
          offsetX,
          colW
        });
      });
    };

    for (let cIdx = 0; cIdx < colCount; cIdx++) {
      const colSlice = sorted.slice(cIdx * chunk, (cIdx + 1) * chunk);
      if (colSlice.length > 0) {
        drawCol(colSlice, cIdx * colW);
      }
    }

    canvas._rowHits = rowHits;
  }, [sorted, dim.w]);

  const onMouseMove = useCallback((e) => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap || !canvas._rowHits) return;
    const r = wrap.getBoundingClientRect();
    const mx = e.clientX - r.left;
    const my = e.clientY - r.top;
    const hit = canvas._rowHits.find(
      (row) => my >= row.y0 && my < row.y1 && mx >= row.offsetX && mx < row.offsetX + row.colW
    );
    if (hit) {
      const { s } = hit;
      const rangeW = (Number(s.max_disposition_days) || 0) - (Number(s.min_disposition_days) || 0);
      setTip({
        show: true,
        x: e.clientX,
        y: e.clientY,
        lines: [
          { bold: true, text: String(s.state_name || s.state) },
          { text: `min ${s.min_disposition_days}d · avg ${s.avg_disposition_days}d · max ${s.max_disposition_days}d` },
          { text: `${s.filing_count} filings · range width ${rangeW}d` }
        ]
      });
    } else setTip((t) => ({ ...t, show: false }));
  }, []);



  if (rows.length === 0) return null;

  return (
    <div>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap', fontSize: '11px', color: 'var(--text3)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ height: '2.5px', width: '22px', borderRadius: '2px', background: '#D3D1C7' }} /> min – max
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: '11px', height: '11px', borderRadius: '50%', border: '2px solid var(--c-orange)', background: 'var(--bg-orange-soft)' }} /> avg
        </span>
      </div>
      <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
        <canvas
          ref={canvasRef}
          role="img"
          aria-label="Disposition days by state: horizontal range from min to max with average marker"
          onMouseMove={onMouseMove}
          onMouseLeave={() => setTip((t) => ({ ...t, show: false }))}
        />
        {tip.show ? (
          <div
            className="map-hover-tooltip"
            style={{
              left: tip.x + 15,
              top: tip.y + 15
            }}
          >
            {(tip.lines || []).map((ln, i) => (
              <div key={i} style={{ fontWeight: ln.bold ? 700 : 400, marginTop: i ? '4px' : 0 }}>
                {ln.text}
              </div>
            ))}
          </div>
        ) : null}
      </div>
      <p style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '10px' }}>
        Wide range = more variable disposition timing. Hover a row for exact values.
      </p>
    </div>
  );
}

const BUBBLE_COLORS = [
  CANONICAL_SPECTRUM[0],
  CANONICAL_SPECTRUM[4],
  CANONICAL_SPECTRUM[8],
  CANONICAL_SPECTRUM[12],
  CANONICAL_SPECTRUM[16],
  CANONICAL_SPECTRUM[20],
  CANONICAL_SPECTRUM[24],
  CANONICAL_SPECTRUM[29]
];

/**
 * Canvas bubble chart (mock chart 4): x = avg objections/filing, y = avg disposition days, r = filings.
 */
export function ObjectionBubbleCanvas({ bubbles }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [dim, setDim] = useState({ w: 640 });
  const [tip, setTip] = useState({ show: false, x: 0, y: 0, title: '', avgObj: '', days: '', count: '' });

  const stats = useMemo(() => {
    return (bubbles || [])
      .map((b, i) => ({
        ...b,
        avgObj: Number(b.avg_objections_per_filing) || 0,
        avgDays: b.avg_disposition_days != null ? Number(b.avg_disposition_days) : null,
        color: BUBBLE_COLORS[i % BUBBLE_COLORS.length],
        short: (b.toi || '').slice(0, 10)
      }))
      .filter((b) => b.avgObj >= 0);
  }, [bubbles]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setDim({ w: el.clientWidth || 640 }));
    ro.observe(el);
    setDim({ w: el.clientWidth || 640 });
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || stats.length === 0) return;

    const W = Math.max(280, dim.w - 8);
    const H = 520;
    const PAD = { t: 24, r: 28, b: 66, l: 66 };
    const pw = W - PAD.l - PAD.r;
    const ph = H - PAD.t - PAD.b;

    const withDays = stats.filter((t) => t.avgDays != null && Number.isFinite(t.avgDays));
    const maxObj = Math.max(...stats.map((t) => t.avgObj), 0.5);
    const maxDays = Math.max(...withDays.map((t) => t.avgDays), 1);
    const maxCount = Math.max(...stats.map((t) => Number(t.filing_count) || 0), 1);

    const axObj = Math.ceil((maxObj * 1.25) / 2) * 2 || 2;
    const axDays = Math.ceil((maxDays * 1.25) / 5) * 5 || 5;

    const xS = (v) => PAD.l + (v / axObj) * pw;
    const yS = (v) => PAD.t + ph - (v / axDays) * ph;
    const rS = (c) => 8 + Math.sqrt((Number(c) || 0) / maxCount) * 32;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const midX = xS(axObj / 2);
    const midY = yS(axDays / 2);

    ctx.fillStyle = 'rgba(147,51,234,0.05)';
    ctx.beginPath();
    ctx.rect(midX, PAD.t, PAD.l + pw - midX, midY - PAD.t);
    ctx.fill();

    ctx.strokeStyle = 'rgba(0,0,0,0.08)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(midX, PAD.t);
    ctx.lineTo(midX, PAD.t + ph);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(PAD.l, midY);
    ctx.lineTo(PAD.l + pw, midY);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.font = "12px system-ui, sans-serif";
    ctx.fillStyle = 'rgba(0,0,0,0.16)';
    ctx.textAlign = 'left';
    ctx.fillText('few obj / fast', PAD.l + 6, PAD.t + ph - 6);
    ctx.textAlign = 'right';
    ctx.fillText('few obj / slow', PAD.l + pw - 6, PAD.t + ph - 6);
    ctx.textAlign = 'left';
    ctx.fillText('many obj / fast', PAD.l + 6, PAD.t + 14);
    ctx.fillStyle = 'rgba(147,51,234,0.4)';
    ctx.textAlign = 'right';
    ctx.fillText('many obj / slow ▲', PAD.l + pw - 6, PAD.t + 14);

    const xTick = Math.max(1, Math.ceil(axObj / 4));
    for (let t = 0; t <= axObj; t += xTick) {
      const x = xS(t);
      ctx.strokeStyle = 'rgba(0,0,0,0.05)';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(x, PAD.t);
      ctx.lineTo(x, PAD.t + ph);
      ctx.stroke();
      ctx.fillStyle = '#9e9e96';
      ctx.font = "13px ui-monospace, monospace";
      ctx.textAlign = 'center';
      ctx.fillText(String(t.toFixed(1)), x, H - PAD.b + 20);
    }

    for (let t = 0; t <= axDays; t += Math.max(5, Math.ceil(axDays / 5))) {
      const y = yS(t);
      ctx.strokeStyle = 'rgba(0,0,0,0.05)';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(PAD.l, y);
      ctx.lineTo(PAD.l + pw, y);
      ctx.stroke();
      ctx.fillStyle = '#9e9e96';
      ctx.font = "13px ui-monospace, monospace";
      ctx.textAlign = 'right';
      ctx.fillText(`${t}d`, PAD.l - 10, y + 4);
    }

    ctx.strokeStyle = 'rgba(0,0,0,0.1)';
    ctx.lineWidth = 0.8;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(PAD.l, PAD.t);
    ctx.lineTo(PAD.l, PAD.t + ph);
    ctx.lineTo(PAD.l + pw, PAD.t + ph);
    ctx.stroke();

    ctx.fillStyle = '#9e9e96';
    ctx.font = "bold 13px system-ui, sans-serif";
    ctx.textAlign = 'center';
    ctx.fillText('avg objections per filing →', PAD.l + pw / 2, H - 10);
    ctx.save();
    ctx.translate(18, PAD.t + ph / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.fillText('avg disposition days →', 0, 0);
    ctx.restore();

    const sorted = [...stats].sort((a, b) => (Number(b.filing_count) || 0) - (Number(a.filing_count) || 0));
    const bubbleHits = [];

    sorted.forEach((t, idx) => {
      const yVal = t.avgDays != null && Number.isFinite(t.avgDays) ? t.avgDays : axDays * 0.08;
      const x = xS(t.avgObj);
      const y = yS(yVal);
      const r = rS(t.filing_count);

      // Restore original vibrant multispectral fill and outline
      ctx.fillStyle = `${t.color}26`; 
      ctx.strokeStyle = t.color;
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Consolidate coordinates for interactive tracking AND prioritized rendering pass
      bubbleHits.push({ x, y, r, t, idx, yVal });
    });



    canvas._bubbleHits = bubbleHits;
  }, [stats, dim.w]);

  const onMove = useCallback((e) => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !canvas._bubbleHits || !wrap) return;
    const r = wrap.getBoundingClientRect();
    const mx = e.clientX - r.left;
    const my = e.clientY - r.top;
    const hit = canvas._bubbleHits.find((b) => Math.hypot(b.x - mx, b.y - my) < b.r + 5);
    canvas.style.cursor = hit ? 'pointer' : 'default';
    if (hit) {
      const { t } = hit;
      setTip({
        show: true,
        x: e.clientX,
        y: e.clientY,
        title: t.toi || 'Unknown TOI',
        avgObj: t.avgObj.toFixed(1),
        days: t.avgDays != null ? `${Number(t.avgDays).toFixed(1)}d` : '—',
        count: t.filing_count
      });
    } else setTip((x) => ({ ...x, show: false }));
  }, []);

  if (stats.length === 0) {
    return <div style={{ color: 'var(--text3)', fontSize: '13px', padding: '16px 0' }}>No TOI bubble data.</div>;
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label="TOI bubbles: objections per filing vs disposition days"
        onMouseMove={onMove}
        onMouseLeave={() => setTip((x) => ({ ...x, show: false }))}
      />
      {tip.show ? (
        <div
          className="map-hover-tooltip"
          style={{
            left: tip.x + 15,
            top: tip.y + 15
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--c-black)', borderBottom: '1px solid var(--border)', paddingBottom: '6px', marginBottom: '6px', maxWidth: '280px', lineHeight: 1.4 }}>{tip.title}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#475569' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span>Avg Objections:</span>
              <strong style={{ color: 'var(--c-black)' }}>{tip.avgObj}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span>Avg Disposition:</span>
              <strong style={{ color: 'var(--c-black)' }}>{tip.days}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginTop: '4px', paddingTop: '4px', borderTop: '1px dashed var(--border)', color: 'var(--text2)' }}>
              <span>Volume:</span>
              <span>{tip.count} filing{tip.count === 1 ? '' : 's'}</span>
            </div>
          </div>
        </div>
      ) : null}
      <p style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '8px' }}>
        Top-right quadrant = relatively high objections and slow disposition. Bubble size scales with filing count.
        {stats.some((s) => s.avgDays == null) ? ' TOIs without disposition data sit near the bottom axis.' : ''}
      </p>
    </div>
  );
}


const TABLE_HEAT_RAMP = [
  { threshold: 0.1,  bg: "#b5dbff", fg: "var(--c-black)" }, // Lightest (Blue-900 text)
  { threshold: 0.3,  bg: "#8ec0f7", fg: "var(--c-black)" }, 
  { threshold: 0.55, bg: "#6aa5ee", fg: "#ffffff" }, // Mid (White text)
  { threshold: 0.78, bg: "#488ae4", fg: "#ffffff" }, 
  { threshold: 1.0,  bg: "#276ed8", fg: "#ffffff" }  // Darkest (White text)
];

const heatBg = (v, max) => {
  // Clean slate neutral for empty cells removes archaic beige residues
  if (!v || max <= 0) return { bg: "var(--c-white)", fg: "var(--text3)" };
  const p = v / max;
  return TABLE_HEAT_RAMP.find(r => p <= r.threshold) || TABLE_HEAT_RAMP[TABLE_HEAT_RAMP.length - 1];
};

/**
 * Self-contained, horizontally scrollable State x TOI Heatmap Table
 */
export function ObjectionHeatmapTable({ stateRows, toiCols, heatLookup, heatmapMax }) {
  const [tip, setTip] = useState({ show: false, x: 0, y: 0, st: "", toi: "", label: "", val: 0 });
  return (
    <div className="report-card" style={{ marginBottom: 0, overflow: "hidden", maxWidth: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", borderBottom: "1px solid var(--border)", paddingBottom: "8px" }}>
        <div className="report-card-title" style={{ margin: 0, border: "none", padding: 0 }}>State (Top 15) x TOI - Objection Count</div>
      </div>
      {heatmapMax > 0 ? (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px", fontSize: "11px", color: "var(--text3)", flexWrap: "wrap" }}>
          <span>Low</span>
          <div style={{ display: "flex", height: "7px", borderRadius: "3px", overflow: "hidden", width: "100px" }}>
            {TABLE_HEAT_RAMP.map((item) => (
              <div key={item.bg} style={{ flex: 1, background: item.bg }} />
            ))}
          </div>
          <span>High</span>
        </div>
      ) : null}
      {stateRows.length === 0 || toiCols.length === 0 ? (
        <div style={{ color: "var(--text3)", fontSize: "13px" }}>No heatmap cells for current filters.</div>
      ) : (
        <div className="objection-heatmap-scroll">
          <table className="objection-heatmap-tbl" style={{ width: "auto", minWidth: "auto" }}>
            <thead>
              <tr>
                <th className="sticky-corner">State</th>
                {toiCols.map((c) => (
                  <th key={c.toi} title={c.label || c.toi} style={{ padding: 0 }}>
                    <div style={{
                      minWidth: "72px",
                      maxWidth: toiCols.length > 1 ? "72px" : "none",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      padding: "6px 8px"
                    }}>
                      {c.label || c.toi || ""}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stateRows.slice(0, 15).map((st) => (
                <tr key={st}>
                  <th className="sticky-state">{st}</th>
                  {toiCols.map((c) => {
                    const v = heatLookup[`${st}|${c.toi}`] || 0;
                    const { bg, fg } = heatBg(v, heatmapMax);
                    return (
                      <td key={`${st}-${c.toi}`}>
                        <div
                          className="objection-heatmap-cell-block"
                          style={{ background: bg, color: fg }}
                          onMouseMove={(e) => setTip({ show: true, x: e.clientX, y: e.clientY, st, toi: c.toi, label: c.label, val: v })}
                          onMouseLeave={() => setTip({ show: false })}
                        >
                          {v || ""}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {tip.show && (
        <div className="map-hover-tooltip" style={{ left: tip.x + 15, top: tip.y + 15 }}>
          <div style={{ fontWeight: 700 }}>{tip.st}</div>
          <div style={{ fontSize: "12px", color: "var(--text2)" }}>{tip.label || tip.toi}</div>
          <div style={{ marginTop: "4px", fontWeight: 600 }}>{tip.val || 0} objection{tip.val !== 1 ? "s" : ""}</div>
        </div>
      )}
    </div>
  );
}
